import { Header, ReviewItem, Title, Footer } from "../components/";
import styles from "./Reviews.module.css";
import { useEffect, useState } from "react";

// Define a unified structure for all reviews
interface UnifiedReview {
  id: string;
  name: string;
  profilePicture: string;
  comment: string;
  date: Date; // Use Date object for accurate sorting
  rating: number;
  sourceLink: string;
}

// Page constants
const FACEBOOK_PAGE_ID = "100075687891593";
const GOOGLE_MAPS_LINK =
  "https://www.google.com/maps/search/?api=1&query=HydroClean+-+Engine+Cleaning&query_place_id=ChIJB1usUmUnxQwRCMLg1xXt7_8";

export const Reviews = () => {
  const [reviews, setReviews] = useState<UnifiedReview[] | null>(null);

  useEffect(() => {
    // Use Promise.all to fetch both data sources concurrently
    Promise.all([
      fetch("/db/facebook_reviews.json").then((res) => res.json()),
      fetch("/db/google_reviews.json").then((res) => res.json()),
    ])
      .then(([facebookData, googleData]) => {
        // --- NORMALIZE FACEBOOK REVIEWS ---
        const transformedFacebook = facebookData.map(
          (r: any): UnifiedReview => ({
            id: `fb-${r.profileName}-${r.date}`,
            name: r.profileName,
            profilePicture: r.profilePicSrc,
            comment: r.comment,
            date: new Date(r.date),
            rating: 5, // Facebook recommendations are positive, default to 5
            sourceLink: `https://www.facebook.com/${FACEBOOK_PAGE_ID}/reviews`,
          }),
        );

        // --- NORMALIZE GOOGLE REVIEWS ---
        const transformedGoogle = googleData
          .filter((r: any) => r.place_name === "HydroClean - Engine Cleaning") // Ensure it's the correct place
          .map(
            (r: any): UnifiedReview => ({
              id: `go-${r.review_id}`,
              name: r.name,
              profilePicture: r.reviewer_photo_url, // Google JSON doesn't provide this, will fallback to placeholder
              comment: r.review_text,
              date: new Date(r.published_at_date),
              rating: r.rating,
              sourceLink: r.review_link,
            }),
          );

        // --- COMBINE, SORT, AND SET STATE ---
        const allReviews = [...transformedFacebook, ...transformedGoogle];

        // Sort all reviews by date, newest first
        allReviews.sort((a, b) => b.date.getTime() - a.date.getTime());

        setReviews(allReviews);
      })
      .catch((error) => {
        console.error("Failed to fetch or process local reviews:", error);
        setReviews([]); // Show an error/empty state
      });
  }, []);

  const getReviewsElement = () => {
    if (reviews === null) {
      return <div className={styles.Loading}>Loading reviews...</div>;
    }
    if (reviews.length === 0) {
      return (
        <div className={styles.noReviews}>
          No reviews available at the moment. Please check back later.
        </div>
      );
    }

    return reviews.map((review) => {
      // Format the date for display
      const prettyDate = review.date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      return (
        <li className={styles.reviewListItem} key={review.id}>
          <ReviewItem
            name={review.name}
            date={prettyDate}
            stars={review.rating}
            comment={review.comment}
            profilePicture={review.profilePicture}
          />
        </li>
      );
    });
  };

  return (
    <div className={styles.reviewsBackground}>
      <Header linksStyle="" />
      <Title text={"What Our Customers Say About Us"} size={28} />
      <div className={styles.leaveReviewCta}>
        <a
          href={`https://www.facebook.com/${FACEBOOK_PAGE_ID}/reviews`}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.leaveReviewButton}
        >
          Review us on Facebook!
        </a>
        <a
          href={GOOGLE_MAPS_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.leaveReviewButton}
        >
          Review us on Google!
        </a>
      </div>
      <ul className={styles.reviewsWrapper}>{getReviewsElement()}</ul>
      <Footer />
    </div>
  );
};
